/**
 * /today/why — the whole picture: every task, where Auto-Mom put it, and why.
 *
 * Owner ask 2026-08-30: "where can I see what automom has mapped for the total
 * set of tasks based on current conditions?" Nowhere, was the answer — four
 * domains each held one column of it and no surface joined them.
 *
 * This is a PULL surface and that is what makes it legal. Feature 4 §5 draws
 * the line at push vs pull, not at how much may be seen: "Whole-plan visibility
 * is permitted on the pull surface. Unsolicited attention remains bounded to
 * one opportunity." /today stays one-at-a-time and veiled by design; this page
 * is the opposite by design, and it is only ever reached deliberately.
 *
 * Two honesty rules it keeps:
 *   1. Every reason says where it came from. A `derived` reason is inference,
 *      not a stored verdict, and the page marks it so — nothing on production
 *      records a per-task reason for holding a task back.
 *   2. It shows the store audit. Nine rationing_* tables are empty globally, so
 *      a screen that silently rendered nothing for them would read as
 *      "Auto-Mom decided nothing" when the truth is "that plane never ran".
 */
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, RefreshCw } from 'lucide-react'
import { BRAND } from '../config/brand'

interface TaskRow {
  task_id: number
  title: string | null
  status: string
  priority: string | null
  life_area: string | null
  due_date: string | null
  state: 'core' | 'placed' | 'surfaced' | 'released' | 'considered' | 'held' | 'done' | 'unmapped'
  plan_rank: number | null
  why: string
  why_source: 'stored' | 'derived' | 'missing'
  last_outcome: string | null
  last_outcome_date: string | null
  gate_action: string | null
  gate_date: string | null
}

interface PlanRow {
  rank: number
  entry_role: string
  day_state: string
  reason_code: string | null
  source_item_type: string
  source_item_id: number
  display_title: string | null
}

interface GateRow {
  created_at: string
  action: string | null
  surfaced: string | null
  reason_codes: string[] | null
  user_visible_reason: string | null
}

interface StateResponse {
  success: boolean
  day: string
  timezone: string
  conditions: Record<string, unknown>
  counts: Record<string, number>
  tasks: TaskRow[]
  plan: PlanRow[]
  gate: GateRow[]
  store_audit: Array<{ store: string; rows: number | null }>
}

/** The order the groups read in: what you are doing, then what is waiting,
 *  then what is finished. Never alphabetical — the sequence is the meaning. */
const GROUPS: Array<{ key: TaskRow['state']; label: string; blurb: string }> = [
  { key: 'core', label: 'Today’s plan', blurb: `${BRAND.assistant} put these on your day.` },
  // Admitted and on today's calendar, but not in the focus plan. The honest
  // answer to "did the orchestrator let it in" — before this group such a
  // task showed as "never evaluated" (2026-09-11).
  { key: 'placed', label: 'On your calendar', blurb: 'Admission placed these on today\u2019s calendar. Not in the focus plan.' },
  { key: 'surfaced', label: 'Offered in the prepared plan', blurb: 'Ready behind the curtain, not yet on your day.' },
  { key: 'released', label: 'Released as optional', blurb: 'Offered to you as an extra, after you finished.' },
  { key: 'considered', label: 'Looked at, passed over', blurb: 'The release gate weighed these and chose something else.' },
  { key: 'held', label: 'Held back', blurb: 'Real work, deliberately not given to you today.' },
  { key: 'unmapped', label: 'Never evaluated', blurb: `${BRAND.assistant} has no decision on these at all.` },
  { key: 'done', label: 'Done', blurb: 'Finished.' },
]

const STATE_TONE: Record<TaskRow['state'], string> = {
  core: 'bg-brand-subtle text-brand border-brand-subtle',
  placed: 'bg-tint-brand-wash text-brand border-tint-brand-edge',
  surfaced: 'bg-warn/10 text-warn border-warn/30',
  released: 'bg-ok/10 text-ok border-ok/30',
  considered: 'bg-surface-alt text-secondary border-soft',
  held: 'bg-surface-alt text-secondary border-soft',
  unmapped: 'bg-surface-alt text-muted border-soft',
  done: 'bg-ok/10 text-ok border-ok/30',
}

function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-app-micro font-bold uppercase tracking-widest text-muted">{label}</dt>
      <dd className="text-app-body text-primary mt-0.5 truncate">{value ?? '—'}</dd>
    </div>
  )
}

export function TodayWhy() {
  const [data, setData] = useState<StateResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await fetch('/api/automom/v1/state', { credentials: 'include' })
      if (!res.ok) throw new Error(`Server responded ${res.status}`)
      const body = (await res.json()) as StateResponse
      if (!body.success) throw new Error('Could not assemble the picture')
      setData(body)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const c = (data?.conditions ?? {}) as Record<string, string | number | null>
  const ceiling = c.ceiling ?? null
  const energy = c.energy_mode ?? null
  // The one contradiction worth calling out on the page rather than in a doc:
  // the energy control writes a row that the plan builder never reads, so a
  // user can sit in 'superhero' under a ceiling of 1 and nothing explains it.
  const energyIgnored = Boolean(energy && energy !== 'steady' && ceiling)

  return (
    <div className="mx-auto max-w-4xl px-4 sm:px-6 py-5 space-y-5">

      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <Link to="/today" className="inline-flex items-center gap-1.5 text-app-meta text-secondary hover:text-primary">
            <ArrowLeft className="h-3.5 w-3.5" /> Today
          </Link>
          <h1 className="text-app-title font-bold text-primary mt-1">Why today looks like this</h1>
          <p className="text-app-meta text-secondary mt-0.5">
            Every task, where {BRAND.assistant} put it, and the reason it landed there.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void load()}
          className="inline-flex min-h-touch shrink-0 items-center gap-1.5 rounded-xl border border-soft px-3 text-app-meta font-medium text-primary hover:bg-surface-alt"
        >
          <RefreshCw className={`h-3.5 w-3.5${loading ? ' animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-2xl border border-err/40 bg-err/5 p-4">
          <p className="text-app-body font-semibold text-primary">We could not assemble the picture.</p>
          <p className="text-app-meta text-secondary mt-0.5">{error}</p>
        </div>
      )}

      {loading && !data && (
        <div className="rounded-2xl border border-soft bg-surface p-8 text-center text-app-meta text-muted">
          Reading what {BRAND.assistant} decided&hellip;
        </div>
      )}

      {data && (
        <>
          {/* ── Conditions ───────────────────────────────────────────── */}
          <section className="rounded-2xl border border-soft bg-surface p-4" aria-label="Today's conditions">
            <h2 className="text-app-meta font-bold uppercase tracking-widest text-secondary">
              The conditions these decisions were made under
            </h2>
            <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
              <Fact label="Day" value={data.day} />
              <Fact label="Energy mode" value={String(energy ?? 'not set')} />
              <Fact label="Ceiling" value={ceiling ? `${ceiling} ${Number(ceiling) === 1 ? 'thing' : 'things'}` : '—'} />
              <Fact label="Recovery mode" value={String(c.recovery_mode ?? 'none')} />
              <Fact label="Your state" value={String(c.user_state ?? 'unknown')} />
              <Fact label="Done today" value={String(c.completions_today ?? '0')} />
              <Fact label="Plan built" value={c.plan_built_at ? String(c.plan_built_at).slice(11, 16) + ' UTC' : 'not built'} />
              <Fact label="Plan shown" value={c.plan_revealed_at ? 'yes' : 'not yet'} />
            </dl>

            {energyIgnored && (
              <p className="mt-3 flex items-start gap-2 rounded-xl border border-warn/30 bg-warn/5 p-2.5 text-app-meta text-secondary">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warn" aria-hidden="true" />
                <span>
                  Your energy mode is <strong className="text-primary">{String(energy)}</strong>, but today&rsquo;s
                  ceiling is <strong className="text-primary">{String(ceiling)}</strong>. The plan is built before
                  the energy mode is read, so changing it today does not change what you were given.
                </span>
              </p>
            )}
          </section>

          {/* ── The tasks ────────────────────────────────────────────── */}
          {GROUPS.map(g => {
            const rows = data.tasks.filter(t => t.state === g.key)
            if (rows.length === 0) return null
            return (
              <section key={g.key} className="rounded-2xl border border-soft bg-surface overflow-hidden" aria-label={g.label}>
                <div className="px-4 py-3 border-b border-soft">
                  <h2 className="text-app-body font-bold text-primary">
                    {g.label} <span className="text-muted font-medium">({rows.length})</span>
                  </h2>
                  <p className="text-app-micro text-muted mt-0.5">{g.blurb}</p>
                </div>
                <div className="divide-y divide-soft">
                  {rows.map(t => (
                    <div key={t.task_id} className="px-4 py-3 flex flex-wrap items-start gap-x-3 gap-y-1.5">
                      <span className={`shrink-0 rounded-md border px-1.5 py-0.5 text-app-micro font-bold uppercase tracking-wide ${STATE_TONE[t.state]}`}>
                        {t.state}{t.plan_rank ? ` · ${t.plan_rank}` : ''}
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="text-app-body text-primary">{t.title || `Task ${t.task_id}`}</p>
                        <p className="text-app-meta text-secondary mt-0.5">
                          {t.why}
                          {t.why_source !== 'stored' && (
                            <span
                              className="ml-1.5 text-muted"
                              title={t.why_source === 'derived'
                                ? `Worked out from the ceiling and your recovery mode — ${BRAND.assistant} does not record a per-task reason for holding something back.`
                                : 'Nothing was recorded for this one.'}
                            >
                              ({t.why_source})
                            </span>
                          )}
                        </p>
                      </div>
                      <span className="shrink-0 text-app-micro text-muted tabular-nums">
                        {t.life_area || ''}{t.due_date ? ` · due ${t.due_date}` : ''}
                      </span>
                    </div>
                  ))}
                </div>
              </section>
            )
          })}

          {/* ── The plan, all entry types ────────────────────────────── */}
          {data.plan.length > 0 && (
            <section className="rounded-2xl border border-soft bg-surface overflow-hidden" aria-label="The prepared plan">
              <div className="px-4 py-3 border-b border-soft">
                <h2 className="text-app-body font-bold text-primary">
                  The prepared plan <span className="text-muted font-medium">({data.plan.length})</span>
                </h2>
                <p className="text-app-micro text-muted mt-0.5">
                  Not only tasks &mdash; the plan also carries goals, skills and invitations.
                </p>
              </div>
              <div className="divide-y divide-soft">
                {data.plan.map(p => (
                  <div key={`${p.rank}-${p.source_item_id}`} className="px-4 py-2.5 flex items-start gap-3">
                    <span className="shrink-0 w-5 text-app-meta text-muted tabular-nums">{p.rank}</span>
                    <div className="min-w-0 flex-1">
                      <p className="text-app-meta text-primary">{p.display_title || `${p.source_item_type} ${p.source_item_id}`}</p>
                      <p className="text-app-micro text-muted mt-0.5">
                        {p.source_item_type} &middot; {p.reason_code || p.entry_role} &middot; {p.day_state.toLowerCase()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── The gate ─────────────────────────────────────────────── */}
          {data.gate.length > 0 && (
            <section className="rounded-2xl border border-soft bg-surface overflow-hidden" aria-label="The release gate">
              <div className="px-4 py-3 border-b border-soft">
                <h2 className="text-app-body font-bold text-primary">What the release gate has been doing</h2>
                <p className="text-app-micro text-muted mt-0.5">Its last five verdicts, in its own words.</p>
              </div>
              <div className="divide-y divide-soft">
                {data.gate.map((g, i) => (
                  <div key={i} className="px-4 py-2.5">
                    <p className="text-app-meta text-primary">{g.user_visible_reason || g.action || 'no reason recorded'}</p>
                    <p className="text-app-micro text-muted mt-0.5 tabular-nums">
                      {String(g.created_at).slice(0, 16).replace('T', ' ')}
                      {g.reason_codes?.length ? ` · ${g.reason_codes.join(', ')}` : ''}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* ── Store audit ──────────────────────────────────────────── */}
          <section className="rounded-2xl border border-soft bg-surface p-4" aria-label="Which parts of the engine wrote anything">
            <h2 className="text-app-meta font-bold uppercase tracking-widest text-secondary">
              Which parts of the engine actually recorded anything
            </h2>
            <p className="text-app-micro text-muted mt-0.5">
              An empty store means that part never ran &mdash; not that {BRAND.assistant} decided nothing.
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
              {data.store_audit.map(s => (
                <li key={s.store} className="flex items-center justify-between gap-3 text-app-meta">
                  <span className="text-secondary truncate">{s.store}</span>
                  <span className={`tabular-nums font-medium ${s.rows ? 'text-primary' : 'text-warn'}`}>
                    {s.rows === null ? 'missing' : s.rows === 0 ? 'empty' : s.rows}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        </>
      )}
    </div>
  )
}

export default TodayWhy
